import type { IsoDate, Language, Millimes } from '@beta-assur/shared';

// Rendu des modèles de messages (spec 05.5 étape 4, spec 06) : variables nommées `{{nom}}`.
// La correspondance avec les paramètres positionnels des modèles WhatsApp ({{1}}, {{2}}...)
// est faite à la soumission pour approbation WhatsApp (BA-031), dans l'ordre d'apparition.

/** Variables disponibles dans un modèle de relance. */
export const TEMPLATE_VARIABLES = [
  'prenom',
  'nom',
  'police',
  'objet',
  'montant',
  'echeance',
  'lien',
  'assureur',
  'agent',
  'suspension',
] as const;
export type TemplateVariable = (typeof TEMPLATE_VARIABLES)[number];

const PLACEHOLDER = /\{\{\s*([a-z_]+)\s*\}\}/g;

export function extractVariables(body: string): string[] {
  return [...new Set([...body.matchAll(PLACEHOLDER)].map((m) => m[1] as string))];
}

export function unknownVariables(body: string): string[] {
  return extractVariables(body).filter(
    (v) => !(TEMPLATE_VARIABLES as readonly string[]).includes(v),
  );
}

export function renderTemplate(
  body: string,
  variables: Partial<Record<string, string>>,
): { text: string; missing: string[] } {
  const missing = extractVariables(body).filter((v) => variables[v] === undefined);
  // Remplacement en une passe : une valeur contenant des accolades n'est pas réinterprétée.
  const text = body.replace(PLACEHOLDER, (_, name: string) => variables[name] ?? '');
  return { text, missing };
}

const CURRENCY_LABEL: Record<Language, string> = { fr: 'DT', ar: 'د.ت' };

/** Millimes en dinars, trois décimales, séparateur de milliers espace : `1 250,500 DT`. */
export function formatAmount(amount: Millimes, language: Language): string {
  const dinars = Math.floor(amount / 1000);
  const millimes = String(amount % 1000).padStart(3, '0');
  const grouped = String(dinars).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${grouped},${millimes} ${CURRENCY_LABEL[language]}`;
}

/** Date calendaire en `jj/mm/aaaa`, usuelle en Tunisie dans les deux langues. */
export function formatDate(date: IsoDate, _language: Language): string {
  const [year, month, day] = date.split('-');
  return `${day}/${month}/${year}`;
}

/**
 * Corps au format positionnel de WhatsApp (`{{1}}`, `{{2}}`...) dans l'ordre d'apparition
 * des variables (BA-031) ; `variables` donne le nom de chaque position.
 */
export function toPositional(body: string): { body: string; variables: string[] } {
  const variables = extractVariables(body);
  return {
    body: body.replace(PLACEHOLDER, (_, name: string) => `{{${variables.indexOf(name) + 1}}}`),
    variables,
  };
}
